import { useEffect, useRef, useState } from 'react'
import { Box, Typography, TextField, Alert, Button } from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckIcon from '@mui/icons-material/Check'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { COLORS } from '../theme'
import { stepHeadingSx, inputSx, textFieldSx, warningBoxSx } from '../styles'
import SpeechToText from '@/components/SpeechToText'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useOnboardingV2 } from '../OnboardingContext'

// ── Types ──────────────────────────────────────────────────

export interface SuggestionItem {
  /** Question text — Suggestions[i] for profile, question_part_N for funding */
  label: string
  /** Optional example text — absent for profile, desc_part_N for funding */
  hint?: string
}

export interface AssessmentGateProps {
  heading: string
  items: SuggestionItem[]
  quality?: string

  inputs: Record<number, string>
  onInputChange: (index: number, value: string) => void
  /** Submit all answers and leave the gate. Called when the user advances past
   *  the last question; the caller owns the resulting navigation. */
  onSubmit: (combinedText: string) => Promise<void>
}

// ── Component ──────────────────────────────────────────────

export default function AssessmentGate({
  heading,
  items,
  quality,
  inputs,
  onInputChange,
  onSubmit,
}: AssessmentGateProps) {
  const { token } = getCurrentUser()
  const { setBeforeNext, setBeforeBack, setCanContinue, setContinueLabel } =
    useOnboardingV2()

  // Stable refs so the nav handlers read the latest answers/submit at call time
  // without re-registering on every keystroke.
  const inputsRef = useRef(inputs)
  inputsRef.current = inputs
  const onSubmitRef = useRef(onSubmit)
  onSubmitRef.current = onSubmit

  const [currentIndex, setCurrentIndex] = useState(0)
  const [insertedSet, setInsertedSet] = useState<Set<number>>(new Set())

  // Reset to the first question whenever a fresh set of suggestions arrives
  // (e.g. the gate re-shows after a resubmit). Keyed on the question text so
  // ordinary re-renders with the same questions don't reset mid-stepping.
  const itemsKey = items.map((i) => i.label).join(' ')
  const [seenKey, setSeenKey] = useState(itemsKey)
  if (itemsKey !== seenKey) {
    setSeenKey(itemsKey)
    setCurrentIndex(0)
  }

  const idx = Math.min(currentIndex, items.length - 1)
  const current = items[idx]
  const isLast = idx === items.length - 1

  const groupRef = useRef<HTMLDivElement>(null)

  // Move focus to the question region on mount and whenever the question
  // changes. This is an SPA transition with no reload, so without an explicit
  // focus move screen-reader/keyboard users wouldn't know the content changed.
  // The region is labelled by the question text; the progress counter is a
  // role="status" live region so the position change is announced too.
  useEffect(() => {
    groupRef.current?.focus()
  }, [idx, itemsKey])

  const handleInsert = () => {
    if (!current?.hint) return
    const existing = inputs[idx] ?? ''
    const appended = existing ? `${existing}\n\n${current.hint}` : current.hint
    onInputChange(idx, appended)
    setInsertedSet((prev) => new Set(prev).add(idx))
  }

  // Drive the shell's single forward/back buttons through the question stepper.
  // Forward advances a question, then submits past the last one; back steps a
  // question, then falls through to the shell's normal step-back at the first.
  useEffect(() => {
    setBeforeNext(async () => {
      if (!isLast) {
        setCurrentIndex(idx + 1)
        return false
      }
      const combined = Object.values(inputsRef.current)
        .filter(Boolean)
        .join('\n\n')
        .trim()
      await onSubmitRef.current(combined)
      return false
    })
    setBeforeBack(async () => {
      if (idx > 0) {
        setCurrentIndex(idx - 1)
        return false
      }
      // First question: let the shell perform the normal step-back.
    })
    return () => {
      setBeforeNext(null)
      setBeforeBack(null)
    }
  }, [idx, isLast, setBeforeNext, setBeforeBack])

  // Single forward button: label reflects skip-vs-advance-vs-submit, and a
  // "Bad" assessment requires at least one answer before the final submit.
  // Depend on the derived booleans, not the inputs object, so this only re-runs
  // when the answered-state actually flips — not on every keystroke.
  const hasCurrent = (inputs[idx] ?? '').trim().length > 0
  const hasAny = Object.values(inputs).some((v) => v.trim())

  useEffect(() => {
    if (!isLast) {
      setContinueLabel(hasCurrent ? 'Next' : 'Skip')
      setCanContinue(true)
    } else {
      setContinueLabel(hasCurrent ? 'Continue' : 'Skip & continue')
      setCanContinue(quality === 'Bad' ? hasAny : true)
    }
    return () => {
      setContinueLabel(null)
      setCanContinue(true)
    }
  }, [idx, isLast, hasCurrent, hasAny, quality, setContinueLabel, setCanContinue])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5" sx={stepHeadingSx}>
        {heading}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          ...warningBoxSx,
          mt: -1,
        }}
      >
        <InfoOutlinedIcon
          aria-hidden="true"
          sx={{
            color: COLORS.warningIcon,
            fontSize: 22,
            mt: 0.25,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body2"
          sx={{ color: COLORS.labelText, lineHeight: 1.6 }}
        >
          The magic of Fundsorter comes from good quality information! We need
          more details so we can provide you with the best matching grant
          opportunities. Trust us, it’s worth it!
        </Typography>
      </Box>

      <Box
        sx={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: 1,
          p: 2.5,
          bgcolor: COLORS.white,
        }}
      >
        <Box
          ref={groupRef}
          tabIndex={-1}
          role="group"
          aria-labelledby="assessment-question"
          sx={{ outline: 'none' }}
        >
          {/* Progress indicator — live region announces the position change */}
          <Typography
            variant="body2"
            role="status"
            sx={{ fontWeight: 600, color: COLORS.primary, mb: 1.5 }}
          >
            Suggestions ({idx + 1} of {items.length})
          </Typography>

          {/* Question */}
          {current?.label && (
            <Typography
              id="assessment-question"
              variant="body2"
              sx={{ fontWeight: 500, color: COLORS.labelText, mb: 2 }}
            >
              Try to answer this question:{' '}
              <span style={{ fontWeight: 400 }}>{current.label}</span>
            </Typography>
          )}

          {/* Suggestion block with Insert */}
          {current?.hint && (
            <Box
              sx={{
                borderLeft: `3px solid ${COLORS.primary}`,
                backgroundColor: '#f5f9ff',
                pl: 2,
                pr: 1,
                py: 1.5,
                borderRadius: 1,
                mb: 2,
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
              >
                <AutoAwesomeIcon
                  aria-hidden="true"
                  sx={{ fontSize: 14, color: COLORS.primary }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: COLORS.primary }}
                >
                  Suggestion
                </Typography>
              </Box>
              <Alert
                severity="info"
                icon={false}
                sx={{ mb: 1, mt: 0.5, backgroundColor: '#fff' }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleInsert}
                    aria-label={
                      insertedSet.has(idx)
                        ? 'Suggestion added'
                        : 'Insert suggestion into answer'
                    }
                  >
                    {insertedSet.has(idx) ? (
                      <>
                        <CheckIcon
                          aria-hidden="true"
                          sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }}
                        />
                        Added
                      </>
                    ) : (
                      'Insert'
                    )}
                  </Button>
                }
              >
                <Typography variant="body2">
                  <span style={{ fontWeight: 600 }}>Consider adding:</span>{' '}
                  &ldquo;{current.hint}&rdquo;
                </Typography>
              </Alert>
            </Box>
          )}

          {token && (
            <SpeechToText
              token={token}
              onTranscription={(text) =>
                onInputChange(
                  idx,
                  inputs[idx] ? `${inputs[idx]}\n\n${text}` : text,
                )
              }
            />
          )}

          {/* Text field for this question */}
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            placeholder="Add more detail here..."
            value={inputs[idx] ?? ''}
            onChange={(e) => onInputChange(idx, e.target.value)}
            sx={textFieldSx}
            slotProps={{
              input: {
                sx: inputSx,
                'aria-label': 'Your answer',
                'aria-describedby': current?.label
                  ? 'assessment-question'
                  : undefined,
              },
            }}
          />
        </Box>
      </Box>

      <Typography
        variant="body2"
        sx={{
          color: COLORS.secondary,
          fontStyle: 'italic',
          textAlign: 'center',
        }}
      >
        {quality === 'Bad'
          ? 'Please answer at least one question to continue. Answering these greatly improves your results.'
          : 'Answering these greatly improves your results. You can skip a question, or use Back to revisit any answer.'}
      </Typography>
    </Box>
  )
}
